import { GenericCallCard, GenericEventCard } from './chunk-FQWCKMXF.js';
import { PANCAKESWAP, PANCAKESWAP_COMPONENTS, PANCAKESWAP_EVENTS, PANCAKESWAP_CALLS, PANCAKESWAP_KINDS, isRecord, ProtocolValue, PANCAKESWAP_FIELDS } from './chunk-KQLD5VJQ.js';
import { displayOf, rawOf, looksLikeAddress } from '@authe/fe-commons/storage';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';

var STEP = "inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-border/60 bg-muted/40 px-1.5 text-3xs font-medium tabular-nums";
var NAME = "text-xs font-medium";
function PancakeSwapCommand({
  decoded,
  display,
  raw
}) {
  const body = isRecord(decoded) ? decoded : void 0;
  if (!body) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  const name = body[PANCAKESWAP_FIELDS.commandName];
  const index = displayOf(body[PANCAKESWAP_FIELDS.commandIndex]);
  const args = body.args;
  const allowRevert = rawOf(body.allowRevert) === true;
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-col gap-1 align-middle", children: [
    /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
      index != null && /* @__PURE__ */ jsx("span", { className: STEP, title: "Position in the plan \u2014 the order is what it does", children: String(index) }),
      /* @__PURE__ */ jsx("span", { className: NAME, children: /* @__PURE__ */ jsx(ProtocolValue, { value: name }) }),
      allowRevert && /* @__PURE__ */ jsx(
        "span",
        {
          className: "text-3xs text-muted-foreground",
          title: "This command may revert without failing the rest of the plan",
          children: "may revert"
        }
      )
    ] }),
    args != null ? /* @__PURE__ */ jsx("span", { className: "min-w-0 pl-1", children: /* @__PURE__ */ jsx(ProtocolValue, { value: args }) }) : /* @__PURE__ */ jsx(
      "span",
      {
        className: "pl-1 text-2xs text-muted-foreground",
        title: "This command's argument layout is not pinned by any observed transaction, so it is named rather than guessed",
        children: "arguments not decoded"
      }
    )
  ] });
}
function feeLabel(fee) {
  fee = rawOf(fee);
  const raw = typeof fee === "string" ? Number(fee) : fee;
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null;
  return `${(raw / 1e4).toFixed(raw % 1e4 === 0 ? 2 : 4).replace(/0+$/, "").replace(/\.$/, "")}%`;
}
var ARROW = "inline-flex items-center gap-1 text-2xs text-muted-foreground";
function PancakeSwapPath({
  decoded,
  display,
  raw,
  chain,
  renderAddress
}) {
  const route = isRecord(decoded) ? decoded : void 0;
  const tokens = Array.isArray(route?.tokens) ? route.tokens : null;
  const fees = Array.isArray(route?.fees) ? route.fees : [];
  if (!tokens || tokens.length === 0) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  return /* @__PURE__ */ jsx("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5 align-middle", children: tokens.map((token, i) => {
    token = rawOf(token);
    const node = typeof token === "string" && looksLikeAddress(token) && renderAddress ? renderAddress(token, { chain }) : /* @__PURE__ */ jsx(ProtocolValue, { value: token });
    const fee = i < tokens.length - 1 ? feeLabel(fees[i]) : null;
    return /* @__PURE__ */ jsxs(React.Fragment, { children: [
      node,
      i < tokens.length - 1 && /* @__PURE__ */ jsx("span", { className: ARROW, title: `Fee tier: ${String(rawOf(fees[i]) ?? "unknown")} (hundredths of a bip)`, children: fee ? `--${fee}-->` : "-->" })
    ] }, `${String(token)}-${i}`);
  }) });
}
var CHIP = "inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-2xs font-medium";
var HOOK_CHIP = "inline-flex items-center gap-1 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-2xs font-medium";
function isZeroAddress(value2) {
  return typeof value2 === "string" && /^0x0{40}$/i.test(value2);
}
function PancakeSwapPoolKey({
  decoded,
  display,
  raw,
  chain,
  renderAddress
}) {
  const key = isRecord(decoded) ? decoded : void 0;
  if (!key) return /* @__PURE__ */ jsx(ProtocolValue, { value: display ?? raw });
  const address = (input) => {
    const value2 = rawOf(input);
    return typeof value2 === "string" && looksLikeAddress(value2) && renderAddress ? renderAddress(value2, { chain }) : /* @__PURE__ */ jsx(ProtocolValue, { value: value2 });
  };
  const hooks = rawOf(key[PANCAKESWAP_FIELDS.hooks]);
  const fee = key[PANCAKESWAP_FIELDS.fee];
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1.5 align-middle", children: [
    address(key[PANCAKESWAP_FIELDS.currency0]),
    /* @__PURE__ */ jsx("span", { className: "text-2xs text-muted-foreground", children: "/" }),
    address(key[PANCAKESWAP_FIELDS.currency1]),
    fee != null && /* @__PURE__ */ jsx("span", { className: CHIP, title: "Fee tier, in hundredths of a basis point", children: /* @__PURE__ */ jsx(ProtocolValue, { value: fee }) }),
    hooks != null && !isZeroAddress(hooks) && /* @__PURE__ */ jsxs(
      "span",
      {
        className: HOOK_CHIP,
        title: "This pool runs a hook, which can charge, rewrite or reject the swap",
        children: [
          "hook ",
          address(hooks)
        ]
      }
    )
  ] });
}

// src/protocols/pancakeswap/index.ts
var PID = PANCAKESWAP.protocolId;
var C = PANCAKESWAP_COMPONENTS;
var value = (kind, component) => ({
  protocolId: PID,
  kind,
  component,
  fields: []
});
var call = (name, component) => ({
  protocolId: PID,
  call: name,
  component,
  summaryFields: []
});
var event = (name, component) => ({
  protocolId: PID,
  event: name,
  component,
  summaryFields: []
});
var PANCAKESWAP_HINTS = {
  valueHints: [
    value(PANCAKESWAP_KINDS.path, C.path),
    value(PANCAKESWAP_KINDS.command, C.command),
    value(PANCAKESWAP_KINDS.poolKey, C.poolKey)
  ],
  callHints: [
    call(PANCAKESWAP_CALLS.swapExactTokensForTokens, C.v2Swap),
    call(PANCAKESWAP_CALLS.exactInput, C.v3Swap),
    call(PANCAKESWAP_CALLS.exactInputSingle, C.v3Swap),
    call(PANCAKESWAP_CALLS.multicall, C.multicall),
    call(PANCAKESWAP_CALLS.execute, C.execute)
  ],
  eventHints: [event(PANCAKESWAP_EVENTS.swap, C.swapEvent)]
};
function registerPancakeSwap(registry) {
  registry.registerValue(C.path, PancakeSwapPath);
  registry.registerValue(C.command, PancakeSwapCommand);
  registry.registerValue(C.poolKey, PancakeSwapPoolKey);
  registry.registerCall(C.v2Swap, GenericCallCard);
  registry.registerCall(C.v3Swap, GenericCallCard);
  registry.registerCall(C.multicall, GenericCallCard);
  registry.registerCall(C.execute, GenericCallCard);
  registry.registerEvent(C.swapEvent, GenericEventCard);
  registry.registerHints(PANCAKESWAP_HINTS);
  return registry;
}

export { PANCAKESWAP_HINTS, PancakeSwapCommand, PancakeSwapPath, PancakeSwapPoolKey, registerPancakeSwap };
//# sourceMappingURL=chunk-4TBRCSWJ.js.map
//# sourceMappingURL=chunk-4TBRCSWJ.js.map